import React, { useState, useEffect, forwardRef, useMemo } from "react";
import { Input, InputRef } from "antd";
import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import { InputProps } from "antd/lib/input";

interface PrivacyMaskedInputProps
  extends Omit<InputProps, "onChange" | "value"> {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  mask: string;
  requireConfirmation?: boolean;
  confirmationClassName?: string;
  onValidationChange?: (isValid: boolean) => void;
}

interface MaskInfo {
  separators: Set<string>;
  separatorPositions: number[];
  maxDigits: number;
  maxLength: number;
}

// Parse mask pattern to extract separator info
function parseMask(mask: string): MaskInfo {
  const separators = new Set<string>();
  const separatorPositions: number[] = [];
  let digitCount = 0;

  for (let i = 0; i < mask.length; i++) {
    const char = mask[i];
    // Digits are represented as '0' or '9' in mask patterns
    if (char === "0" || char === "9") {
      digitCount++;
    } else {
      separators.add(char);
      separatorPositions.push(i);
    }
  }

  return {
    separators, // Keep as Set for O(1) lookups
    separatorPositions,
    maxDigits: digitCount,
    maxLength: mask.length,
  };
}

// IMPORTANT: We use forwardRef to properly integrate with Form.Item
const PrivacyMaskedInput = forwardRef<InputRef, PrivacyMaskedInputProps>(
  (
    {
      value,
      onChange,
      placeholder,
      disabled,
      className,
      mask,
      requireConfirmation,
      confirmationClassName,
      onValidationChange,
      ...restProps
    },
    ref
  ) => {
    const [isVisible, setIsVisible] = useState<boolean>(false);
    const [internalValue, setInternalValue] = useState<string>(value || "");
    const [confirmationValue, setConfirmationValue] = useState<string>("");
    const [confirmationVisible, setConfirmationVisible] =
      useState<boolean>(false);
    const [originalValue, setOriginalValue] = useState<string>(value || "");

    // Parse mask once
    const maskInfo = useMemo(() => parseMask(mask), [mask]);

    // Format value according to mask pattern
    const formatValue = (val: string): string => {
      const cleaned = val.replace(/\D/g, "");
      let formatted = "";
      let digitIndex = 0;

      for (let i = 0; i < mask.length && digitIndex < cleaned.length; i++) {
        const maskChar = mask[i];
        if (maskChar === "0" || maskChar === "9") {
          formatted += cleaned[digitIndex++];
        } else {
          formatted += maskChar;
        }
      }

      return formatted;
    };

    // Mask the value with bullets
    const maskValue = (val: string): string => {
      if (!val) return "";
      // Replace all digits with bullets, keep separators
      return val.replace(/\d/g, "•");
    };

    // Determine what to display - ensure prefilled values are immediately masked
    const currentInternalValue =
      value !== undefined ? value || "" : internalValue;
    const displayValue = isVisible
      ? currentInternalValue
      : maskValue(currentInternalValue);
    const confirmationDisplayValue = confirmationVisible
      ? confirmationValue
      : maskValue(confirmationValue);

    // Helper function to create input handler for both main and confirmation inputs
    const createInputHandler =
      (
        currentValue: string,
        setCurrentValue: (value: string) => void,
        isVisible: boolean,
        displayValue: string,
        onChangeCallback?: (value: string) => void
      ) =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;

        // If the user is typing in the masked state, we need to handle it differently
        if (!isVisible && currentValue) {
          // Handle backspace/delete
          if (inputValue.length < displayValue.length) {
            // Get the current digits from the actual value
            const currentDigits = currentValue.replace(/\D/g, "");

            // Find the start and end visual positions of the deletion
            const { startPos, endPos } = findDeletedRange(
              displayValue,
              inputValue
            );

            // Map visual positions to digit indices
            // Count only non-separator characters before each position
            let startDigitIndex = 0;
            let endDigitIndex = 0;

            for (let i = 0; i < displayValue.length; i++) {
              const char = displayValue[i];
              const isSeparator = maskInfo.separators.has(char);

              if (i < startPos && !isSeparator) {
                startDigitIndex++;
              }
              if (i < endPos && !isSeparator) {
                endDigitIndex++;
              }
            }

            // Remove the digits in that range
            const newDigits =
              currentDigits.slice(0, startDigitIndex) +
              currentDigits.slice(endDigitIndex);

            const formatted = formatValue(newDigits);
            setCurrentValue(formatted);
            if (onChangeCallback) onChangeCallback(formatted);
            return;
          }

          // For new input, append to the existing value
          const lastChar = inputValue.slice(-1);
          if (/\d/.test(lastChar)) {
            const currentDigits = currentValue.replace(/\D/g, "");
            if (currentDigits.length >= maskInfo.maxDigits) return;

            const newDigits = currentDigits + lastChar;
            const formatted = formatValue(newDigits);
            setCurrentValue(formatted);

            if (onChangeCallback) {
              onChangeCallback(formatted);
            }
          }
          return;
        }

        // Normal input handling when visible
        const cleaned = inputValue.replace(/\D/g, "");

        // Limit to max digits
        if (cleaned.length > maskInfo.maxDigits) return;

        const formatted = formatValue(cleaned);
        setCurrentValue(formatted);

        if (onChangeCallback) {
          onChangeCallback(formatted);
        }
      };

    // Helper function to find the range of deleted characters
    const findDeletedRange = (
      before: string,
      after: string
    ): { startPos: number; endPos: number } => {
      let startPos = 0;
      let endPos = before.length;

      // Find the start position where strings differ
      for (let i = 0; i < Math.min(before.length, after.length); i++) {
        if (before[i] !== after[i]) {
          startPos = i;
          break;
        }
        if (i === after.length - 1) {
          // All of 'after' matched, deletion is at the end
          startPos = after.length;
          break;
        }
      }

      // Find the end position by comparing from the back
      let beforeIdx = before.length - 1;
      let afterIdx = after.length - 1;
      while (afterIdx >= startPos && beforeIdx >= startPos) {
        if (before[beforeIdx] !== after[afterIdx]) {
          break;
        }
        beforeIdx--;
        afterIdx--;
      }
      endPos = beforeIdx + 1;

      return { startPos, endPos };
    };


    // Sync with external value changes (from Form.Item)
    useEffect(() => {
      if (value !== undefined && value !== internalValue) {
        setInternalValue(value || "");
        // Only update originalValue if this is the first time we're setting it (prefill scenario)
        if (!originalValue && value) {
          setOriginalValue(value);
        }
      }
    }, [value, internalValue, originalValue]);

    // Validate confirmation when either value changes
    useEffect(() => {
      if (requireConfirmation) {
        // Check if confirmation field should be visible
        const shouldShowConfirmation =
          currentInternalValue.length === maskInfo.maxLength && // Value is complete
          (originalValue ? currentInternalValue !== originalValue : true); // Different from saved OR new entry

        if (!shouldShowConfirmation) {
          // Confirmation field is hidden, validation passes
          if (onValidationChange) {
            onValidationChange(true);
          }
        } else {
          // Confirmation field is visible, validate it
          if (!confirmationValue) {
            if (onValidationChange) {
              onValidationChange(false);
            }
          } else {
            // If confirmation has value, check if it matches
            const isValid = currentInternalValue === confirmationValue;
            if (onValidationChange) {
              onValidationChange(isValid);
            }
          }
        }
      } else {
        if (onValidationChange) {
          onValidationChange(true);
        }
      }
    }, [
      currentInternalValue,
      confirmationValue,
      requireConfirmation,
      onValidationChange,
      originalValue,
      maskInfo.maxLength,
    ]);

    // Clear confirmation value when value changes from original (confirmation field appears)
    useEffect(() => {
      if (
        requireConfirmation &&
        originalValue &&
        currentInternalValue !== originalValue
      ) {
        // Value has changed from original, clear confirmation to force re-entry
        setConfirmationValue("");
      }
    }, [requireConfirmation, originalValue, currentInternalValue]);
    // Note: confirmationValue is intentionally NOT in dependencies to avoid clearing on every keystroke

    const toggleVisibility = () => {
      setIsVisible(!isVisible);
    };

    const toggleConfirmationVisibility = () => {
      setConfirmationVisible(!confirmationVisible);
    };

    // Create specific handlers for main and confirmation inputs
    const handleChange = createInputHandler(
      currentInternalValue,
      setInternalValue,
      isVisible,
      displayValue,
      onChange
    );

    const handleConfirmationChange = createInputHandler(
      confirmationValue,
      setConfirmationValue,
      confirmationVisible,
      confirmationDisplayValue
    );

    return (
      <>
        <Input
          {...restProps}
          ref={ref}
          value={displayValue}
          onChange={handleChange}
          placeholder={placeholder || maskValue(mask)}
          disabled={disabled}
          className={className}
          maxLength={maskInfo.maxLength}
          suffix={
            <button
              type="button"
              onClick={toggleVisibility}
              style={{
                border: "none",
                background: "none",
                cursor: "pointer",
                padding: "4px",
                display: "flex",
                alignItems: "center",
                color: "#8c8c8c",
              }}
              disabled={disabled}
            >
              {isVisible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
            </button>
          }
        />
        {/* Confirmation Input - Only rendered if requireConfirmation is true and the user
        has completed typing the original input. In addition, this field is not rendered on return trips
        to the step unless the user modifies the saved value. This is not a form item (it has no ref),
        so it will not be saved to the application. If the values do not match, an error message will be
        displayed. */}
        {requireConfirmation &&
          currentInternalValue.length === maskInfo.maxLength &&
          (originalValue ? currentInternalValue !== originalValue : true) && (
            <div style={{ marginTop: "8px" }}>
              <Input
                value={confirmationDisplayValue}
                onChange={handleConfirmationChange}
                placeholder={"Confirm value"}
                disabled={disabled}
                className={confirmationClassName}
                maxLength={maskInfo.maxLength}
                suffix={
                  <button
                    type="button"
                    onClick={toggleConfirmationVisibility}
                    style={{
                      border: "none",
                      background: "none",
                      cursor: "pointer",
                      padding: "4px",
                      display: "flex",
                      alignItems: "center",
                      color: "#8c8c8c",
                    }}
                    disabled={disabled}
                  >
                    {confirmationVisible ? (
                      <EyeInvisibleOutlined />
                    ) : (
                      <EyeOutlined />
                    )}
                  </button>
                }
              />
            </div>
          )}
      </>
    );
  }
);

PrivacyMaskedInput.displayName = "PrivacyMaskedInput";

export default PrivacyMaskedInput;
